import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowRight,
  BarChart3,
  Calendar,
  CalendarCheck,
  Check,
  CheckCircle,
  Clock,
  CreditCard,
  Crown,
  Headphones,
  Image,
  ListChecks,
  Loader2,
  MapPin,
  Package,
  Palette,
  RefreshCw,
  Users,
} from 'lucide-react';
import {
  cancelSubscription,
  getSubscriptionHistory,
  updateVendorSubscriptionAutoRenew,
  useVendorSubscriptionFeatures,
} from '../../services/subscriptionService';
import { useToastContext } from '../../contexts/ToastContext';
import RenewalModal from '../../components/shared/RenewalModal';
import { VendorPageHeader, VendorGuideCallout } from '../../components/vendor/guidance';
import { VENDOR_PAGE_GUIDES } from '../../utils/vendorPageGuides';

const PLAN_FEATURES = [
  { key: 'feature_analytics', label: 'Advanced analytics', icon: BarChart3 },
  { key: 'feature_custom_branding', label: 'Custom branding', icon: Palette },
  { key: 'feature_priority_support', label: 'Priority support', icon: Headphones },
  { key: 'feature_advanced_booking', label: 'Advanced booking', icon: CalendarCheck },
  { key: 'feature_multi_location', label: 'Multi-location', icon: MapPin },
];

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return 'N/A';
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const formatMoney = (amount, currency = 'USD') => {
  const num = Number(amount);
  if (!Number.isFinite(num)) return '—';
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: String(currency || 'USD').toUpperCase(),
      minimumFractionDigits: 2,
    }).format(num);
  } catch {
    return `$${num.toFixed(2)}`;
  }
};

const statusStyles = (status) => {
  switch (status) {
    case 'active':
      return 'text-emerald-700 bg-emerald-50 border-emerald-100 dark:text-emerald-300 dark:bg-emerald-950/40 dark:border-emerald-800/50';
    case 'trial':
      return 'text-sky-700 bg-sky-50 border-sky-100 dark:text-sky-300 dark:bg-sky-950/40 dark:border-sky-800/50';
    case 'expired':
    case 'cancelled':
      return 'text-red-700 bg-red-50 border-red-100 dark:text-red-300 dark:bg-red-950/40 dark:border-red-800/50';
    default:
      return 'text-gray-700 bg-gray-100 border-gray-200 dark:text-gray-300 dark:bg-gray-800 dark:border-gray-700';
  }
};

const VendorMySubscription = () => {
  const { success, error: toastError } = useToastContext();
  const {
    hasActiveSubscription,
    currentSubscription: sub,
    isChecking,
    isLoading,
    refetch,
    checkSubscriptionStatus,
  } = useVendorSubscriptionFeatures();

  const [timeLeft, setTimeLeft] = useState(null);
  const [showRenewalModal, setShowRenewalModal] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);

  const { data: historyResponse, isLoading: historyLoading } = useQuery({
    queryKey: ['vendorSubscriptionHistory'],
    queryFn: getSubscriptionHistory,
    staleTime: 2 * 60 * 1000,
  });
  const history = historyResponse?.data || [];

  const calculateTimeLeft = useCallback(() => {
    if (!sub?.end_date) return null;
    const endDate = new Date(sub.end_date);
    const difference = endDate - new Date();
    if (difference <= 0) return null;
    return {
      days: Math.floor(difference / (1000 * 60 * 60 * 24)),
      hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
      minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
      seconds: Math.floor((difference % (1000 * 60)) / 1000),
    };
  }, [sub?.end_date]);

  useEffect(() => {
    if (!sub?.end_date) {
      setTimeLeft(null);
      return undefined;
    }
    const tick = () => setTimeLeft(calculateTimeLeft());
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [sub?.end_date, calculateTimeLeft]);

  const autoRenewMutation = useMutation({
    mutationFn: (next) => updateVendorSubscriptionAutoRenew(next),
    onSuccess: async (res) => {
      success('Auto-renewal', res?.message || 'Preference saved.');
      await refetch();
      checkSubscriptionStatus();
    },
    onError: (err) => {
      toastError(err?.message || 'Could not update auto-renewal.');
    },
  });

  const cancelMutation = useMutation({
    mutationFn: cancelSubscription,
    onSuccess: async (res) => {
      success('Subscription', res?.message || 'Subscription cancelled.');
      setConfirmCancel(false);
      await refetch();
      checkSubscriptionStatus();
    },
    onError: (err) => {
      toastError(err?.message || 'Could not cancel subscription.');
    },
  });

  const featuresList = useMemo(() => {
    const raw = sub?.features_list || [];
    return raw.filter((item) => {
      const lower = String(item).toLowerCase();
      const isCapacity =
        /(booking|service|portfolio|image)/.test(lower) &&
        /(up to|unlimited|\d+\s*\/|\d+\s*(booking|service|item))/.test(lower);
      return !isCapacity;
    });
  }, [sub?.features_list]);

  const daysRemaining = sub?.days_remaining;
  const isExpiringSoon =
    daysRemaining != null && daysRemaining <= 5 && daysRemaining > 0;
  const urgency =
    daysRemaining == null
      ? null
      : daysRemaining <= 1
        ? 'critical'
        : daysRemaining <= 3
          ? 'high'
          : daysRemaining <= 5
            ? 'medium'
            : null;

  const limits = sub?.limits || {};
  const usage = sub?.usage || {};
  const features = sub?.features || {};
  const formatLimit = (value) => (value == null ? 'Unlimited' : value);
  const currency = sub?.billing_currency || 'USD';
  const intervalLabel =
    sub?.billing_interval === 'yearly'
      ? 'year'
      : sub?.billing_interval === 'monthly'
        ? 'month'
        : sub?.billing_interval || null;

  const usageRows = [
    {
      icon: Users,
      label: 'Bookings this month',
      value: `${usage.bookings_this_month ?? 0} / ${formatLimit(limits.max_bookings_per_month)}`,
      warn: sub?.at_booking_limit,
    },
    {
      icon: Package,
      label: 'Services',
      value: `${usage.services_count ?? 0} / ${formatLimit(limits.max_services)}`,
      warn: sub?.at_service_limit,
    },
    {
      icon: Image,
      label: 'Portfolio items',
      value: `${usage.portfolio_count ?? 0} / ${formatLimit(limits.max_portfolio_items)}`,
      warn: sub?.at_portfolio_limit,
    },
  ];

  const guide = VENDOR_PAGE_GUIDES.mySubscription;

  if (isChecking || isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600 mx-auto mb-3" />
          <p className="text-sm text-gray-500 dark:text-gray-400">Loading subscription…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-5xl pb-10">
      <VendorPageHeader
        title="Subscription"
        subtitle="See your plan, time remaining, and manage auto-renewal."
        actions={
          <button
            type="button"
            onClick={() => refetch()}
            className="inline-flex items-center gap-2 px-3.5 py-2 text-sm font-medium rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        }
      />

      {guide ? (
        <VendorGuideCallout title={guide.title} steps={guide.steps} icon={ListChecks} />
      ) : null}

      {!hasActiveSubscription || !sub ? (
        <div className="rounded-2xl border border-amber-200/80 dark:border-amber-800/50 bg-amber-50/70 dark:bg-amber-950/20 p-6 sm:p-8">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center shrink-0">
              <Crown className="w-6 h-6 text-amber-700 dark:text-amber-300" />
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="text-lg font-semibold text-amber-950 dark:text-amber-100">
                No active subscription
              </h2>
              <p className="mt-1 text-sm text-amber-800/90 dark:text-amber-200/80 leading-relaxed">
                Choose a plan to accept bookings, unlock features, and keep your vendor profile live.
              </p>
            </div>
            <Link
              to="/vendor/subscription"
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 text-sm font-semibold shrink-0"
            >
              View plans
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      ) : (
        <>
          {isExpiringSoon && (
            <div
              className={`rounded-2xl border px-4 py-3.5 flex flex-col sm:flex-row sm:items-center gap-3 ${
                urgency === 'critical'
                  ? 'border-red-200 bg-red-50 dark:border-red-800/50 dark:bg-red-950/30'
                  : 'border-amber-200 bg-amber-50 dark:border-amber-800/50 dark:bg-amber-950/30'
              }`}
            >
              <AlertTriangle
                className={`w-5 h-5 shrink-0 ${
                  urgency === 'critical' ? 'text-red-600' : 'text-amber-700'
                }`}
              />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-gray-900 dark:text-gray-50">
                  {urgency === 'critical'
                    ? 'Your plan expires very soon'
                    : `Your plan renews / expires in ${daysRemaining} day${daysRemaining === 1 ? '' : 's'}`}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
                  {sub.auto_renew
                    ? 'Auto-renewal is on — you’ll be charged before the period ends.'
                    : 'Auto-renewal is off. Renew or turn it on to avoid interruption.'}
                </p>
              </div>
              {!sub.is_free_tier && (
                <button
                  type="button"
                  onClick={() => setShowRenewalModal(true)}
                  className="inline-flex items-center justify-center gap-2 px-3.5 py-2 rounded-xl bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 text-sm font-semibold shrink-0"
                >
                  Renew now
                </button>
              )}
            </div>
          )}

          <section className="grid grid-cols-1 lg:grid-cols-5 gap-4">
            <div className="lg:col-span-3 rounded-2xl border border-gray-200/80 dark:border-gray-700/80 bg-white dark:bg-gray-900 shadow-sm p-5 sm:p-6">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400">
                    Current plan
                  </p>
                  <h2 className="mt-1 text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-50">
                    {sub.plan_name}
                  </h2>
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <span
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border capitalize ${statusStyles(
                        sub.is_in_trial ? 'trial' : sub.status
                      )}`}
                    >
                      {sub.is_in_trial || sub.status === 'active' ? (
                        <CheckCircle className="w-3.5 h-3.5" />
                      ) : (
                        <Clock className="w-3.5 h-3.5" />
                      )}
                      {sub.is_in_trial ? 'Trial' : sub.status}
                    </span>
                    {sub.is_free_tier && (
                      <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-medium border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300">
                        Free tier
                      </span>
                    )}
                    {sub.stripe_managed && (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border border-sky-200 dark:border-sky-800/50 text-sky-800 dark:text-sky-300 bg-sky-50 dark:bg-sky-950/40">
                        <CreditCard className="w-3 h-3" />
                        Stripe billing
                      </span>
                    )}
                  </div>
                </div>
                <div className="w-11 h-11 rounded-xl bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 flex items-center justify-center shrink-0">
                  <Crown className="w-5 h-5 text-gray-700 dark:text-gray-200" />
                </div>
              </div>

              <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="rounded-xl bg-gray-50 dark:bg-gray-800/60 px-4 py-3">
                  <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Price
                  </p>
                  <p className="mt-1 text-lg font-semibold text-gray-900 dark:text-gray-50 tabular-nums">
                    {sub.is_free_tier
                      ? 'Free'
                      : `${formatMoney(sub.plan_price ?? sub.amount_paid, currency)}${
                          intervalLabel ? ` / ${intervalLabel}` : ''
                        }`}
                  </p>
                </div>
                <div className="rounded-xl bg-gray-50 dark:bg-gray-800/60 px-4 py-3">
                  <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Started
                  </p>
                  <p className="mt-1 text-sm font-semibold text-gray-900 dark:text-gray-50">
                    {formatDate(sub.start_date)}
                  </p>
                </div>
                <div className="rounded-xl bg-gray-50 dark:bg-gray-800/60 px-4 py-3">
                  <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    {sub.auto_renew ? 'Renews on' : 'Expires on'}
                  </p>
                  <p className="mt-1 text-sm font-semibold text-gray-900 dark:text-gray-50">
                    {formatDate(sub.end_date)}
                  </p>
                </div>
              </div>

              <div className="mt-5 flex flex-wrap gap-2">
                <Link
                  to="/vendor/subscription"
                  className="inline-flex items-center gap-2 px-3.5 py-2 text-sm font-medium rounded-xl border border-gray-200 dark:border-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  Compare plans
                  <ArrowRight className="w-3.5 h-3.5" />
                </Link>
                {!sub.is_free_tier && (
                  <button
                    type="button"
                    onClick={() => setShowRenewalModal(true)}
                    className="inline-flex items-center gap-2 px-3.5 py-2 text-sm font-medium rounded-xl bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 hover:bg-black dark:hover:bg-white"
                  >
                    <RefreshCw className="w-3.5 h-3.5" />
                    Renew / extend
                  </button>
                )}
              </div>
            </div>

            <div
              className={`lg:col-span-2 rounded-2xl border shadow-sm p-5 sm:p-6 ${
                isExpiringSoon
                  ? urgency === 'critical'
                    ? 'border-red-200 bg-red-50/80 dark:border-red-800/50 dark:bg-red-950/20'
                    : 'border-amber-200 bg-amber-50/80 dark:border-amber-800/50 dark:bg-amber-950/20'
                  : 'border-gray-200/80 dark:border-gray-700/80 bg-white dark:bg-gray-900'
              }`}
            >
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400">
                Time remaining
              </p>
              {timeLeft ? (
                <div className="mt-4 grid grid-cols-4 gap-2">
                  {[
                    { label: 'Days', value: timeLeft.days },
                    { label: 'Hours', value: timeLeft.hours },
                    { label: 'Min', value: timeLeft.minutes },
                    { label: 'Sec', value: timeLeft.seconds },
                  ].map((unit) => (
                    <div
                      key={unit.label}
                      className="text-center rounded-xl bg-white/90 dark:bg-gray-900/70 border border-gray-200/80 dark:border-gray-700/60 py-3"
                    >
                      <div className="text-xl sm:text-2xl font-bold tabular-nums text-gray-900 dark:text-gray-50">
                        {unit.value}
                      </div>
                      <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                        {unit.label}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="mt-4 text-base font-semibold text-red-600 dark:text-red-400">
                  Expired
                </p>
              )}
              <div className="mt-4 flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                <Calendar className="w-4 h-4 shrink-0" />
                <span>
                  {daysRemaining != null && daysRemaining > 0
                    ? `${daysRemaining} calendar day${daysRemaining === 1 ? '' : 's'} left`
                    : 'Period ended'}
                </span>
              </div>
            </div>
          </section>

          <section className="rounded-2xl border border-gray-200/80 dark:border-gray-700/80 bg-white dark:bg-gray-900 shadow-sm p-5 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <div className="min-w-0">
                <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400">
                  Billing
                </p>
                <h3 className="mt-1 text-lg font-semibold text-gray-900 dark:text-gray-50">
                  Auto-renewal
                </h3>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400 leading-relaxed max-w-xl">
                  {sub.auto_renew
                    ? sub.stripe_managed
                      ? 'Your card on file will be charged at the end of this period. Turn this off to cancel future renewals while keeping access until the end date.'
                      : 'We’ll renew this plan when the period ends. Turn off to stop automatic renewal.'
                    : 'Auto-renewal is off. Access continues until the end date, then the plan will lapse unless you renew.'}
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={!!sub.auto_renew}
                disabled={autoRenewMutation.isPending || sub.is_free_tier}
                onClick={() => autoRenewMutation.mutate(!sub.auto_renew)}
                className={`relative inline-flex h-8 w-14 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${
                  sub.auto_renew
                    ? 'bg-emerald-600'
                    : 'bg-gray-300 dark:bg-gray-600'
                }`}
              >
                <span
                  className={`inline-block h-6 w-6 transform rounded-full bg-white shadow transition ${
                    sub.auto_renew ? 'translate-x-7' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
            {sub.is_free_tier && (
              <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                Free plans don’t use paid auto-renewal. Upgrade to a paid plan for recurring billing.
              </p>
            )}
            {autoRenewMutation.isPending && (
              <p className="mt-3 text-xs text-gray-500 inline-flex items-center gap-1.5">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Saving preference…
              </p>
            )}
          </section>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <section className="rounded-2xl border border-gray-200/80 dark:border-gray-700/80 bg-white dark:bg-gray-900 shadow-sm p-5 sm:p-6">
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400">
                Usage
              </p>
              <h3 className="mt-1 text-lg font-semibold text-gray-900 dark:text-gray-50 mb-4">
                Plan limits
              </h3>
              <div className="space-y-3">
                {usageRows.map((row) => {
                  const Icon = row.icon;
                  return (
                    <div
                      key={row.label}
                      className={`flex items-center justify-between gap-3 rounded-xl px-3 py-2.5 ${
                        row.warn
                          ? 'bg-amber-50 dark:bg-amber-950/30 border border-amber-200/80 dark:border-amber-800/40'
                          : 'bg-gray-50 dark:bg-gray-800/60'
                      }`}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <Icon className="w-4 h-4 text-gray-400 shrink-0" />
                        <span className="text-sm text-gray-700 dark:text-gray-300 truncate">
                          {row.label}
                        </span>
                      </div>
                      <span className="text-sm font-semibold text-gray-900 dark:text-gray-100 tabular-nums shrink-0">
                        {row.value}
                      </span>
                    </div>
                  );
                })}
              </div>
            </section>

            <section className="rounded-2xl border border-gray-200/80 dark:border-gray-700/80 bg-white dark:bg-gray-900 shadow-sm p-5 sm:p-6">
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400">
                Included
              </p>
              <h3 className="mt-1 text-lg font-semibold text-gray-900 dark:text-gray-50 mb-4">
                Features
              </h3>
              <div className="space-y-2">
                {featuresList.length > 0
                  ? featuresList.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-2.5">
                        <Check className="h-4 w-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
                        <span className="text-sm text-gray-800 dark:text-gray-200">{item}</span>
                      </div>
                    ))
                  : PLAN_FEATURES.map((feature) => {
                      const Icon = feature.icon;
                      const included = !!features[feature.key];
                      return (
                        <div key={feature.key} className="flex items-center gap-2.5">
                          {included ? (
                            <Check className="h-4 w-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
                          ) : (
                            <span className="h-4 w-4 shrink-0 text-gray-400 text-center leading-none">
                              —
                            </span>
                          )}
                          <Icon className="h-4 w-4 text-gray-400 shrink-0" />
                          <span
                            className={`text-sm ${
                              included
                                ? 'text-gray-900 dark:text-gray-100 font-medium'
                                : 'text-gray-500'
                            }`}
                          >
                            {feature.label}
                          </span>
                        </div>
                      );
                    })}
              </div>
            </section>
          </div>

          <section className="rounded-2xl border border-gray-200/80 dark:border-gray-700/80 bg-white dark:bg-gray-900 shadow-sm overflow-hidden">
            <div className="px-5 sm:px-6 pt-5 pb-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400">
                History
              </p>
              <h3 className="mt-1 text-lg font-semibold text-gray-900 dark:text-gray-50">
                Past subscriptions
              </h3>
            </div>
            {historyLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
              </div>
            ) : history.length === 0 ? (
              <div className="px-5 sm:px-6 pb-6 text-sm text-gray-500 dark:text-gray-400">
                No subscription history yet.
              </div>
            ) : (
              <div className="divide-y divide-gray-100 dark:divide-gray-800">
                {history.slice(0, 8).map((row) => (
                  <div
                    key={row.id}
                    className="px-5 sm:px-6 py-3.5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-900 dark:text-gray-50">
                        {row.plan_name}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {formatDate(row.start_date)} → {formatDate(row.end_date)}
                      </p>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span
                        className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold border capitalize ${statusStyles(
                          row.status
                        )}`}
                      >
                        {row.status}
                      </span>
                      <span className="text-sm font-semibold tabular-nums text-gray-900 dark:text-gray-50">
                        {formatMoney(row.amount_paid, row.currency || currency)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {!sub.is_free_tier && (
            <section className="rounded-2xl border border-red-200/70 dark:border-red-900/40 bg-red-50/40 dark:bg-red-950/15 p-5 sm:p-6">
              <h3 className="text-sm font-semibold text-red-900 dark:text-red-200">
                Cancel subscription
              </h3>
              <p className="mt-1 text-sm text-red-800/90 dark:text-red-300/90 leading-relaxed max-w-2xl">
                Prefer to keep access until the end date? Turn off auto-renewal above instead.
                Cancelling ends the plan immediately and may remove paid features right away.
              </p>
              {!confirmCancel ? (
                <button
                  type="button"
                  onClick={() => setConfirmCancel(true)}
                  className="mt-4 inline-flex items-center px-3.5 py-2 text-sm font-medium rounded-xl border border-red-300 dark:border-red-800 text-red-800 dark:text-red-200 hover:bg-red-100/80 dark:hover:bg-red-950/40"
                >
                  Cancel immediately…
                </button>
              ) : (
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    disabled={cancelMutation.isPending}
                    onClick={() => cancelMutation.mutate()}
                    className="inline-flex items-center gap-2 px-3.5 py-2 text-sm font-semibold rounded-xl bg-red-700 hover:bg-red-800 text-white disabled:opacity-60"
                  >
                    {cancelMutation.isPending ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : null}
                    Confirm cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmCancel(false)}
                    className="inline-flex items-center px-3.5 py-2 text-sm font-medium rounded-xl border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200"
                  >
                    Keep plan
                  </button>
                </div>
              )}
            </section>
          )}
        </>
      )}

      <RenewalModal
        isOpen={showRenewalModal}
        onClose={() => setShowRenewalModal(false)}
        subscriptionDetails={sub}
        onSuccess={async () => {
          setShowRenewalModal(false);
          await refetch();
          checkSubscriptionStatus();
        }}
      />
    </div>
  );
};

export default VendorMySubscription;
